import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle, ArrowLeft } from 'lucide-react';
import apiService from '../services/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifying, setIsVerifying] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  const [userInfo, setUserInfo] = useState(null);

  // Verificar token al cargar la página
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError('Token de recuperación no válido');
        setIsVerifying(false);
        return;
      }

      try {
        const response = await apiService.verifyResetToken(token);
        if (response.valid) {
          setUserInfo(response.user);
        } else {
          setError(response.message || 'El enlace de recuperación no es válido o ha expirado');
        }
      } catch (error) {
        setError('Error al verificar el enlace de recuperación');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpiar error cuando el usuario empiece a escribir
    if (error) {
      setError('');
    }
  };

  const validatePassword = (password) => {
    const minLength = password.length >= 6;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    
    return {
      isValid: minLength && hasUpperCase && hasLowerCase && hasNumbers,
      errors: {
        minLength: !minLength,
        hasUpperCase: !hasUpperCase,
        hasLowerCase: !hasLowerCase,
        hasNumbers: !hasNumbers
      }
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.password || !formData.confirmPassword) {
      setError('Por favor completa todos los campos');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.isValid) {
      setError('La contraseña no cumple con los requisitos de seguridad');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await apiService.resetPassword(token, formData.password);
      
      if (response.success) {
        setIsSuccess(true);
        // Redirigir al login después de 3 segundos
        setTimeout(() => {
          navigate('/mi-cuenta', { state: { mode: 'login' } });
        }, 3000);
      } else {
        setError(response.message || 'Error al restablecer la contraseña');
      }
    } catch (error) {
      setError(error.message || 'Error al restablecer la contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerifying) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="card text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold text-text mb-2">Verificando enlace...</h2>
            <p className="text-gray-600">Por favor espera mientras verificamos tu enlace de recuperación</p>
          </div>
        </div>
      </div>
    );
  }

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="card text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-text mb-4">¡Contraseña Restablecida!</h2>
            <p className="text-gray-600 mb-6">
              Tu contraseña ha sido actualizada exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
            </p>
            
            <div className="space-y-3">
              <Link
                to="/mi-cuenta"
                state={{ mode: 'login' }}
                className="btn-primary w-full hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Ir al Login
              </Link>
              
              <p className="text-sm text-gray-500">
                Serás redirigido automáticamente en unos segundos...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error && !userInfo) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full">
          <div className="card text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            
            <h2 className="text-2xl font-bold text-text mb-4">Enlace Inválido</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            
            <div className="space-y-3">
              <Link
                to="/mi-cuenta"
                state={{ mode: 'forgot-password' }}
                className="btn-primary w-full hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Solicitar Nuevo Enlace
              </Link>
              
              <Link
                to="/mi-cuenta"
                state={{ mode: 'login' }}
                className="btn-outline w-full hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg"
              >
                Volver al Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const passwordValidation = validatePassword(formData.password);

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="card">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-text mb-2">Nueva Contraseña</h2>
            <p className="text-gray-600">
              Hola <strong>{userInfo?.username}</strong>, crea una nueva contraseña segura
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              </div>
            )}

            <div>
              <label className="form-label">
                Nueva Contraseña
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="input-field pl-10 pr-10"
                  placeholder="Ingresa tu nueva contraseña"
                  disabled={isLoading}
                  required
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {/* Indicadores de validación de contraseña */}
              {formData.password && (
                <div className="mt-2 space-y-1">
                  <div className={`flex items-center space-x-2 text-xs ${passwordValidation.errors.minLength ? 'text-red-600' : 'text-green-600'}`}>
                    <div className={`w-2 h-2 rounded-full ${passwordValidation.errors.minLength ? 'bg-red-500' : 'bg-green-500'}`}></div>
                    <span>Mínimo 6 caracteres</span>
                  </div>
                  <div className={`flex items-center space-x-2 text-xs ${passwordValidation.errors.hasUpperCase ? 'text-red-600' : 'text-green-600'}`}>
                    <div className={`w-2 h-2 rounded-full ${passwordValidation.errors.hasUpperCase ? 'bg-red-500' : 'bg-green-500'}`}></div>
                    <span>Una letra mayúscula</span>
                  </div>
                  <div className={`flex items-center space-x-2 text-xs ${passwordValidation.errors.hasLowerCase ? 'text-red-600' : 'text-green-600'}`}>
                    <div className={`w-2 h-2 rounded-full ${passwordValidation.errors.hasLowerCase ? 'bg-red-500' : 'bg-green-500'}`}></div>
                    <span>Una letra minúscula</span>
                  </div>
                  <div className={`flex items-center space-x-2 text-xs ${passwordValidation.errors.hasNumbers ? 'text-red-600' : 'text-green-600'}`}>
                    <div className={`w-2 h-2 rounded-full ${passwordValidation.errors.hasNumbers ? 'bg-red-500' : 'bg-green-500'}`}></div>
                    <span>Un número</span>
                  </div>
                </div>
              )}
            </div>

            <div>
              <label className="form-label">
                Confirmar Contraseña
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="input-field pl-10 pr-10"
                  placeholder="Confirma tu nueva contraseña"
                  disabled={isLoading}
                  required
                />
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              
              {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                <p className="text-red-600 text-xs mt-1">Las contraseñas no coinciden</p>
              )}
            </div>

            <button
              type="submit"
              disabled={isLoading || !passwordValidation.isValid || formData.password !== formData.confirmPassword}
              className="btn-primary w-full hover:scale-105 active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isLoading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Restableciendo...</span>
                </div>
              ) : (
                'Restablecer Contraseña'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/mi-cuenta"
              state={{ mode: 'login' }}
              className="text-primary hover:text-red-600 text-sm font-medium transition-colors duration-200 flex items-center justify-center space-x-1"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Volver al Login</span>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
